import React, { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { posSetup } from '../api/client';

/**
 * Whether this build is running on an installed till, and what that till's licence and branch are.
 *
 * The same React bundle serves the back office and the desktop application, so "am I a till?" is a
 * question about the server, not about the build — it comes from `/pos-setup/status`, which is
 * unauthenticated and answers before anyone has logged in.
 *
 * A till is deliberately not the whole product: it shows the branch's products and fiscalization
 * and nothing else, so the machine at the counter is not loading pages a cashier will never open.
 */
const PosContext = createContext({ isPos: false, isLoading: true, status: null, refetch: () => {} });

export function PosProvider({ children }) {
  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['pos-setup-status'],
    queryFn: () => posSetup.status().then(r => r.data),
    // A missing endpoint means an older server; carry on as a normal cloud client.
    retry: false,
    staleTime: Infinity,
  });

  const value = {
    isPos: !!status?.isPosMode,
    isActivated: !!status?.isActivated,
    // Decided in the cloud and mirrored down by the sync — never locally (see PosSyncEngine).
    licenseValid: !!status?.licenseValid,
    branchName: status?.branchName ?? null,
    branchId: status?.branchId ?? null,
    isLoading,
    status: status ?? null,
    refetch,
  };

  return <PosContext.Provider value={value}>{children}</PosContext.Provider>;
}

export const usePos = () => useContext(PosContext);

/**
 * The only pages an installed till has: selling (fiscalization) and the branch's products.
 * Settings stays because operating the till needs it — forcing a sync, refreshing the catalogue,
 * unbinding the PC — and it renders only the till's own cards there. Everything else (invoices,
 * orders, clients, branches, workers, billing, dashboard, EC declarations, the admin console) is
 * back-office work and is never even loaded here.
 */
export const POS_ROUTES = ['/fiscalization', '/products', '/settings'];

/** Where a till starts and where it lands when something sends it elsewhere. */
export const POS_HOME = '/fiscalization';
